import { useCallback, useEffect, useState } from "react"
import { Button } from "@ciq/shared/components/buttons"
import { SegmentSelect } from "@ciq/shared/components/select"
import { useGroupedJobHistory } from "./context"
import { SchoolIssuesSummary } from "./SchoolIssuesSummary"

/**
 * @typedef {Object} FilterOption
 * @property {string} id - Filter identifier ("all", "running", or "issues")
 * @property {string} label - Display label for the filter
 * @property {number} count - Number of items matching this filter
 * @property {boolean} [disabled] - Whether the filter option is disabled
 */

/**
 * @typedef {Object} JobEntry
 * @property {string} job - Job type name
 * @property {string|null} job_date - Job date (null if missing/skipped)
 * @property {string|null} id - Job ID (null if missing/skipped)
 * @property {string|null} glue_id - Glue job ID (null if missing/skipped)
 * @property {string} status - Job status (Finished, Error, Running, Staged, Queued, Missing, Stale, Skipped, No Data)
 * @property {string} [skip_reason] - Reason why job was skipped (e.g., "Weekdays only")
 */

/**
 * @typedef {Object} DateCardData
 * @property {string} job_date - Date in ISO format
 * @property {string} date_label - Human-readable date label
 * @property {Object} summary - Summary with number_of_runs and labels
 * @property {number} summary.number_of_runs - Number of runs/chains for this date
 * @property {Array<string>} summary.labels - Status labels ("Completed" or "Error")
 * @property {Object} flags - Status flags
 * @property {boolean} flags.has_running - Has running jobs
 * @property {boolean} flags.has_failures - Has failed jobs
 * @property {boolean} flags.has_missing - Has missing jobs
 * @property {boolean} flags.has_stale - Has stale jobs
 * @property {Array<JobEntry>} jobs - Flat list of job statuses
 */

/**
 * @typedef {Object} Semester
 * @property {string} id - Semester ID
 * @property {string} name - Semester name (e.g., "Fall 2025")
 * @property {string} start_date - Start date in ISO format
 * @property {string} end_date - End date in ISO format
 */

/**
 * Determine which period a date falls into based on semesters.
 * @param {string} dateStr - Date in ISO format (YYYY-MM-DD)
 * @param {Array<Semester>} semesters - Array of semester objects
 * @returns {{type: string, name: string}} Period info with type ("semester" or "break") and name
 */
function getPeriodForDate(dateStr, semesters) {
  if (!semesters || semesters.length === 0) {
    return { type: "unknown", name: "" }
  }

  const date = new Date(dateStr)

  // Check if date falls within any semester
  for (const sem of semesters) {
    const start = new Date(sem.start_date)
    const end = new Date(sem.end_date)
    if (date >= start && date <= end) {
      return { type: "semester", name: sem.name }
    }
  }

  // Date is between semesters - find which break it's in
  // Sort semesters by start_date descending (most recent first)
  const sorted = [...semesters].sort((a, b) => new Date(b.start_date) - new Date(a.start_date))

  for (let i = 0; i < sorted.length; i++) {
    const currentSem = sorted[i]
    const nextSem = sorted[i + 1]

    const currentStart = new Date(currentSem.start_date)
    const currentEnd = new Date(currentSem.end_date)

    // Date is before this semester but after the previous one (or no previous)
    if (date < currentStart) {
      if (nextSem) {
        const nextEnd = new Date(nextSem.end_date)
        if (date > nextEnd) {
          // Between semesters
          return { type: "break", name: `Break (${nextSem.name} → ${currentSem.name})` }
        }
      } else {
        // Before all semesters
        return { type: "break", name: `Before ${currentSem.name}` }
      }
    }

    // Date is after this semester
    if (date > currentEnd && !nextSem) {
      return { type: "break", name: `After ${currentSem.name}` }
    }
  }

  return { type: "break", name: "Between Semesters" }
}

/**
 * Build filter options with counts for SegmentSelect.
 * @param {Object} filterCounts - Counts for each filter
 * @returns {Array<FilterOption>} Filter options for SegmentSelect
 */
function getFilterOptions(filterCounts) {
  return [
    { id: "all", label: "All", count: filterCounts.all },
    { id: "running", label: "Running", count: filterCounts.running, disabled: filterCounts.running === 0 },
    { id: "issues", label: "Issues", count: filterCounts.issues, disabled: filterCounts.issues === 0 },
  ]
}

/**
 * Get CSS class for job status.
 * @param {string} status - Job status
 * @returns {string} CSS class suffix
 */
export function getStatusClass(status) {
  switch (status) {
    case "Finished":
    case "Skipped":
      return "completed"
    case "Error":
    case "No Data":
      return "failed"
    case "Running":
      return "in-progress"
    case "Staged":
      return "staged"
    case "Queued":
      return "queued"
    case "Missing":
      return "missing"
    case "Stale":
      return "failed"
    default:
      return "pending"
  }
}

/**
 * Render a summary badge showing completion status.
 * @param {Object} props
 * @param {Object} props.summary - Summary object with number_of_runs and labels
 * @param {Object} props.flags - Status flags
 * @param {Array<JobEntry>} props.jobs - Jobs array for counting
 */
function SummaryBadge({ summary, flags, jobs }) {
  const { number_of_runs, labels } = summary
  const { has_running, has_failures, has_missing, has_stale } = flags

  // Count jobs by status (Skipped counts as completed, No Data is a failure)
  const finishedCount = jobs.filter(j => ["Finished", "Skipped"].includes(j.status)).length
  const totalExpected = jobs.length

  if (totalExpected === 0) {
    return <span className="summary-badge summary-empty">No jobs</span>
  }

  // Has failures (Error or No Data statuses)
  if (has_failures) {
    const failedCount = jobs.filter(j => ["Error", "No Data"].includes(j.status)).length
    return (
      <span className="summary-badge summary-error">
        {failedCount} failed, {finishedCount}/{totalExpected} done
      </span>
    )
  }

  // Has stale jobs
  if (has_stale) {
    const staleCount = jobs.filter(j => j.status === "Stale").length
    return (
      <span className="summary-badge summary-warning">
        {staleCount} stale, {finishedCount}/{totalExpected} done
      </span>
    )
  }

  // Has missing jobs
  if (has_missing) {
    const missingCount = jobs.filter(j => j.status === "Missing").length
    return (
      <span className="summary-badge summary-warning">
        {missingCount} missing, {finishedCount}/{totalExpected} done
      </span>
    )
  }

  // Has running/queued/staged jobs
  if (has_running) {
    const runningCount = jobs.filter(j => j.status === "Running").length
    const queuedCount = jobs.filter(j => j.status === "Queued").length
    const stagedCount = jobs.filter(j => j.status === "Staged").length

    // Show most concerning state first
    if (queuedCount > 0) {
      return (
        <span className="summary-badge summary-queued">
          {queuedCount} queued, {finishedCount}/{totalExpected} done
        </span>
      )
    }
    if (stagedCount > 0) {
      return (
        <span className="summary-badge summary-staged">
          {stagedCount} staged, {finishedCount}/{totalExpected} done
        </span>
      )
    }
    return (
      <span className="summary-badge summary-running">
        {runningCount} running, {finishedCount}/{totalExpected} done
      </span>
    )
  }

  // All completed
  if (labels.includes("Completed")) {
    return (
      <span className="summary-badge summary-success">
        {finishedCount}/{totalExpected} completed
      </span>
    )
  }

  // Default fallback
  return (
    <span className="summary-badge summary-pending">
      {finishedCount}/{totalExpected} done
    </span>
  )
}

/**
 * Render a single date card showing job summary.
 * @param {Object} props
 * @param {DateCardData} props.dateCard - Date card data from API
 * @param {boolean} props.isExpanded - Whether this card is expanded
 * @param {Function} props.onToggle - Callback to toggle expansion
 */
function DateCard({ dateCard, isExpanded, onToggle }) {
  const { date_label, summary, flags = {}, jobs = [] } = dateCard
  const { has_failures, has_running, has_missing, has_stale } = flags

  // Get missing job names for tooltip
  const missingJobNames = jobs
    .filter(j => j.status === "Missing")
    .map(j => j.job)

  return (
    <div className={`date-card ${isExpanded ? "date-card-expanded" : ""}`}>
      <button
        className="date-card-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <span className={`date-card-caret ${isExpanded ? "date-card-caret-open" : ""}`}>
          &#9656;
        </span>
        <div className="date-card-title">
          <span className="date-card-date">{date_label}</span>
          {has_failures && <span className="date-card-warning" title="Has failures">!</span>}
          {has_stale && <span className="date-card-stale" title="Has stale jobs">~</span>}
        </div>
        <div className="date-card-summary">
          {summary.number_of_runs > 1 && (
            <span className="date-card-chains">{summary.number_of_runs} runs</span>
          )}
          {has_running && <span className="summary-badge summary-running">Running</span>}
          <SummaryBadge summary={summary} flags={flags} jobs={jobs} />
        </div>
      </button>

      {isExpanded && (
        <div className="date-card-content">
          {has_missing && (
            <div className="missing-jobs-notice">
              <span className="missing-jobs-icon">⚠</span>
              <span className="missing-jobs-label">Missing jobs:</span>
              <span className="missing-jobs-list">{missingJobNames.join(", ")}</span>
            </div>
          )}
          <div className="jobs-list">
            {jobs.map(job => (
              <div key={job.job} className="job-entry">
                <span className="job-name">{job.job}</span>
                <span className={`status-badge status-${getStatusClass(job.status)}`}>
                  {job.status}
                  {job.skip_reason && <span className="skip-reason"> ({job.skip_reason})</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Grouped Job History view component.
 * Shows date cards with summary status and drill-down capability.
 *
 * @returns {JSX.Element} The JobHistoryGrouped component
 */
/**
 * Group date cards by their period (semester or break).
 * @param {Array<DateCardData>} dates - Array of date card data
 * @param {Array<Semester>} semesters - Array of semesters
 * @returns {Array<{period: {type: string, name: string}, dates: Array<DateCardData>}>} Grouped dates
 */
function groupDatesByPeriod(dates, semesters) {
  if (!dates || dates.length === 0) return []

  const groups = []
  let currentGroup = null

  for (const dateCard of dates) {
    const period = getPeriodForDate(dateCard.job_date, semesters)

    // Start a new group if period changed
    if (!currentGroup || currentGroup.period.name !== period.name) {
      currentGroup = { period, dates: [] }
      groups.push(currentGroup)
    }

    currentGroup.dates.push(dateCard)
  }

  return groups
}

/**
 * Check if any date cards in the group have issues.
 * @param {Array<DateCardData>} dates - Date cards to check
 * @returns {{hasIssues: boolean, issueCount: number}} Issue status
 */
function getGroupIssueStatus(dates) {
  let issueCount = 0
  for (const dateCard of dates) {
    const flags = dateCard.flags || {}
    const { has_failures, has_missing, has_stale } = flags
    if (has_failures || has_missing || has_stale) {
      issueCount++
    }
  }
  return { hasIssues: issueCount > 0, issueCount }
}

/**
 * Get display label for a period.
 * @param {Object} period - Period info with type and name
 * @returns {string} Display label
 */
function getPeriodLabel(period) {
  if (period.type === "semester") {
    return period.name
  }
  if (period.type === "break") {
    return "Between Semesters"
  }
  return "No Semester"
}

/**
 * Render a semester group container with label.
 * All groups are collapsible.
 * @param {Object} props
 * @param {Object} props.period - Period info with type and name
 * @param {Array<DateCardData>} props.dates - Date cards in this group
 * @param {React.ReactNode} props.children - Child elements (date cards)
 */
function SemesterGroup({ period, dates, children }) {
  const isSemester = period.type === "semester"
  const [isExpanded, setIsExpanded] = useState(false)

  const { hasIssues, issueCount } = getGroupIssueStatus(dates)

  // All groups show error state when issues exist, otherwise use type-specific styling
  let statusClass
  if (hasIssues) {
    statusClass = "semester-group-error"
  } else if (isSemester) {
    statusClass = "semester-group-success"
  } else {
    statusClass = "semester-group-neutral"
  }

  const handleToggle = () => {
    setIsExpanded(prev => !prev)
  }

  const label = getPeriodLabel(period)

  return (
    <div className={`semester-group semester-group-${period.type} ${statusClass}`}>
      <button
        className="semester-group-header"
        onClick={handleToggle}
        aria-expanded={isExpanded}
      >
        <span className={`semester-group-caret ${isExpanded ? "semester-group-caret-open" : ""}`}>
          &#9656;
        </span>
        <span className="semester-group-label">{label}</span>
        {hasIssues && (
          <span className="semester-group-summary">
            <span className="semester-group-badge semester-group-badge-error">
              {issueCount} {issueCount === 1 ? "day" : "days"} with issues
            </span>
          </span>
        )}
      </button>
      {isExpanded && (
        <div className="semester-group-content">
          {children}
        </div>
      )}
    </div>
  )
}

// Export pure functions for testing
export { getPeriodForDate, getFilterOptions, groupDatesByPeriod, getGroupIssueStatus, getPeriodLabel }

export function JobHistoryGrouped() {
  const { isLoaded, dates, expandedDate, filter, filterCounts, semesters, actions } = useGroupedJobHistory()

  useEffect(() => {
    actions.loadDates()
  }, [actions])

  const handleRefresh = useCallback(() => {
    actions.refresh()
  }, [actions])

  const handleFilterChange = useCallback(option => {
    actions.setFilter(option.id)
  }, [actions])

  const filterOptions = getFilterOptions(filterCounts)

  if (!isLoaded) {
    return (
      <div className="job-history-loading" data-testid="job-history-loading">
        Loading job history...
      </div>
    )
  }

  return (
    <div className="job-history-grouped">
      <SchoolIssuesSummary onFilterChange={actions.setFilter} />

      <div className="job-history-grouped-header">
        <SegmentSelect
          options={filterOptions}
          selectedId={filter}
          onChange={handleFilterChange}
        />
        <Button variant="secondary" onClick={handleRefresh}>
          Refresh
        </Button>
      </div>

      <div className="date-cards">
        {dates.length === 0 && (
          <div className="date-cards-empty">
            No job history found.
          </div>
        )}
        {groupDatesByPeriod(dates, semesters).map((group, groupIndex) => (
          <SemesterGroup key={`${group.period.type}-${group.period.name}-${groupIndex}`} period={group.period} dates={group.dates}>
            {group.dates.map(dateCard => (
              <DateCard
                key={dateCard.job_date}
                dateCard={dateCard}
                isExpanded={expandedDate === dateCard.job_date}
                onToggle={() => actions.setExpandedDate(dateCard.job_date)}
              />
            ))}
          </SemesterGroup>
        ))}
      </div>
    </div>
  )
}
